import React, { useState, useEffect, useCallback } from "react";
import { roomOptions, roomLoadError } from "../../helpers/roomOptions";
import { lookupGuestByMobile } from "../../helpers/guestLookup";
import { Table, Button, Modal, Form } from "react-bootstrap";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import {
  addSpaAppointment,
  getSpaAppointment,
  updateSpaAppointment,
  deleteSpaAppointment,
  getSpaService,
  getAllGuests,
  gethotelinfoIdAPI,
  createSpaBilling,
  updateSpaBilling,
  getSpaBillingByAppointment,
  BASE_URL_for_images,
  getActiveReservations,
} from "../../helpers/api.ts";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  Container,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb.js";
import SpaAppointmentStatusBadge from "../../customcomponents/SpaAppointmentStatusBadge";
import SpaAppointmentExcelExport from "../../customcomponents/SpaAppointmentExcelExport";

// ─────────────────────────────────────────────────────────────────────────────
// Payment Status Badge
// ─────────────────────────────────────────────────────────────────────────────
const PaymentStatusBadge = ({ status }) => {
  if (!status) return <span style={{ color: "#bbb", fontSize: "11px" }}>—</span>;
  const cfg = {
    PAID    : { bg: "#e8f5e9", color: "#1b5e20", border: "#43a047", label: "Paid"    },
    UNPAID  : { bg: "#fff3e0", color: "#bf360c", border: "#fb8c00", label: "Unpaid"  },
    PENDING : { bg: "#fff8e1", color: "#f57f17", border: "#fdd835", label: "Pending" },
  };
  const s = cfg[status] || cfg.UNPAID;
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: "20px",
      fontSize: "11px", fontWeight: 700, letterSpacing: "0.3px",
      background: s.bg, color: s.color, border: `1.5px solid ${s.border}`,
    }}>
      {s.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const SpaAppointments = () => {
  const [appointments,          setAppointments]          = useState([]);
  const [services,              setServices]              = useState([]);
  const [guests,                setGuests]                = useState([]);
  const [showModal,             setShowModal]             = useState(false);
  const [editingAppointment,    setEditingAppointment]    = useState(null);
  const [formData,              setFormData]              = useState({});
  const [isNameEditable,        setIsNameEditable]        = useState(true);
  const [showDeleteConfirmModal,setShowDeleteConfirmModal]= useState(false);
  const [appointmentToDelete,   setAppointmentToDelete]   = useState(null);
  const [hotelDetails,          setHotelDetails]          = useState(null);
  const navigation = useNavigate();

  const [pagination,    setPagination]    = useState({ page: 1, limit: 10, search: "", nationality: "", gender: "" });
  const [paginationInfo,setPaginationInfo]= useState({ total: 0, currentPage: 1, totalPages: 1 });

  const [showBillingModal,   setShowBillingModal]   = useState(false);
  const [billingStep,        setBillingStep]        = useState(1);
  const [billingData,        setBillingData]        = useState({
    gst_percentage: 0, subtotal: 0, discount: 0, final_amount: 0,
    payment_status: "UNPAID", appointment: null,
  });
  const [isEditingBilling,   setIsEditingBilling]   = useState(false);
  const [existingBillingId,  setExistingBillingId]  = useState(null);
  const [spaBillingData,     setSpaBillingData]     = useState({});
  const [availableReservations,setAvailableReservations]= useState([]);
  const [showReservationLookup,setShowReservationLookup]= useState(false);

  // ── GST mode state ──────────────────────────────────────────────────────
  // "per_service" | "bill_level"
  const [gstMode,          setGstMode]          = useState("bill_level");
  const [billGstRate,      setBillGstRate]      = useState(0);      // % e.g. 18
  const [editableGstRates, setEditableGstRates] = useState({});     // per-service { [id]: pct }

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  let hotel_id1;
  if (user?.role === "HOTEL_ADMIN") { hotel_id1 = user?.hotel_id; }
  else { hotel_id1 = user?.hotel?.hotel_id; }

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────

  const parseServiceIds = (appointment) => {
    let ids = appointment.service_ids;
    if (typeof ids === "string") {
      try { ids = JSON.parse(ids); } catch { ids = null; }
    }
    if (!Array.isArray(ids) || ids.length === 0) {
      ids = appointment.service_id ? [appointment.service_id] : [];
    }
    return ids.map(Number).filter(Boolean);
  };

  const getSelectedServices = () => {
    if (!billingData.appointment) return [];
    return parseServiceIds(billingData.appointment)
      .map((sid) => services.find((s) => Number(s.id) === sid))
      .filter(Boolean);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Core calculation — supports both modes
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * recalcFinalAmount
   * Works for BOTH gst_mode values.
   * @param {number}  subtotal
   * @param {number}  discount
   * @param {"per_service"|"bill_level"} mode
   * @param {object}  gstRates      — { [svc.id]: pct }  (per_service mode)
   * @param {number}  billRate      — pct e.g. 18          (bill_level mode)
   * @param {Array}   selectedSvcs
   * @returns {{ finalAmount, totalGst, gstPercentage }}
   */
  const recalcFinalAmount = (subtotal, discount, mode, gstRates, billRate, selectedSvcs) => {
    const netTotal = parseFloat((subtotal - discount).toFixed(2));
    let totalGst;

    if (mode === "bill_level") {
      totalGst = parseFloat((netTotal * (billRate / 100)).toFixed(2));
    } else {
      // per_service — proportional discount, then GST per line
      totalGst = selectedSvcs.reduce((sum, svc) => {
        const price      = parseFloat(svc?.price) || 0;
        const lineDisc   = subtotal > 0 ? (price / subtotal) * discount : 0;
        const netLine    = price - lineDisc;
        const gstRate    = (gstRates[svc.id] ?? parseFloat(svc.gst || 0)) / 100;
        return sum + netLine * gstRate;
      }, 0);
      totalGst = parseFloat(totalGst.toFixed(2));
    }

    const finalAmount   = parseFloat((netTotal + totalGst).toFixed(2));
    const gstPercentage = netTotal > 0
      ? parseFloat(((totalGst / netTotal) * 100).toFixed(2)) : 0;
    return { finalAmount, totalGst, gstPercentage };
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Fetch helpers
  // ─────────────────────────────────────────────────────────────────────────

  const fetchHotelDetails = async () => {
    try {
      if (!hotel_id1) { toast.error("Hotel ID is missing"); return; }
      const response = await gethotelinfoIdAPI(hotel_id1);
      if (response?.success && response?.data) setHotelDetails(response.data);
    } catch { toast.error("Failed to fetch hotel details"); }
  };

  const fetchBillingForAppointment = useCallback(async (appointment) => {
    try {
      const response = await getSpaBillingByAppointment(appointment.id);
      if (response?.success && response?.data) {
        setSpaBillingData((prev) => ({ ...prev, [appointment.id]: response.data }));
        return response.data;
      }
      return null;
    } catch { return null; }
  }, []);

  const fetchAllBillingData = useCallback(async (list) => {
    await Promise.all(list.map((a) => fetchBillingForAppointment(a)));
  }, [fetchBillingForAppointment]);

  useEffect(() => {
    fetchAppointments(); fetchServices(); fetchGuests(); fetchHotelDetails();
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    const urlParams   = new URLSearchParams(window.location.search);
    const reservationId = urlParams.get("reservation_id");
    const guestId       = urlParams.get("guest_id");
    const guestName     = urlParams.get("guest_name");
    const billingType   = urlParams.get("billing_type");
    if (reservationId && guestId) {
      setFormData({
        reservation_id: reservationId, guest_id: guestId,
        name: guestName || "", billing_type: billingType || "room",
        mobile_number: "", service_id: "", therapist_name: "",
        appointment_time: "", status: "PENDING",
      });
      setIsNameEditable(false);
      setShowModal(true);
    }
  }, []);

  const fetchAvailableReservations = async () => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      const hotel_id = u?.role === "RECEPTIONIST" ? u?.hotel?.hotel_id : u?.hotel_id;
      const response = await getActiveReservations(hotel_id ?? u?.hotel?.hotel_id ?? u?.hotel_id);
      if (response.success) setAvailableReservations(response.reservations || []);
      else toast.error(roomLoadError(null, response));
    } catch (error) { toast.error(roomLoadError(error)); }
  };

  const fetchAppointments = async () => {
    try {
      const response = await getSpaAppointment({ hotel_id: hotel_id1, page: pagination.page, limit: pagination.limit });
      const list = response.appointments || [];
      setAppointments(list);
      if (list.length > 0) await fetchAllBillingData(list);
      if (response.pagination) {
        setPaginationInfo({
          total      : response.pagination.total       || 0,
          currentPage: response.pagination.currentPage || 1,
          totalPages : response.pagination.totalPages  || 1,
        });
      }
    } catch { toast.error("Failed to fetch appointments"); }
  };

  const fetchServices = async () => {
    try {
      const response = await getSpaService({ hotel_id: hotel_id1 });
      setServices(response.spaServices || []);
    } catch (e) { console.log(e); }
  };

  const fetchGuests = async () => {
    try {
      const response = await getAllGuests({ pagination, hotel_id: hotel_id1 });
      if (response.data?.pagination) setPaginationInfo(response.data.pagination);
      setGuests(response.guests || []);
    } catch (e) { console.error(e); }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Appointment modal
  // ─────────────────────────────────────────────────────────────────────────

  const handlePageChange       = (page) => setPagination((p) => ({ ...p, page }));
  const handleShowModal        = (appt = null) => { setEditingAppointment(appt); setFormData(appt || {}); setIsNameEditable(!appt); setShowModal(true); };
  const handleCloseModal       = () => { setShowModal(false); setEditingAppointment(null); setFormData({}); };
  const handleInputChange      = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleServiceChange    = (sel) => {
    const ids = sel ? sel.map((o) => o.value) : [];
    setFormData({ ...formData, service_ids: ids, service_id: ids[0] || null });
  };

  const handleMobileNumberChange = async (e) => {
    const mobile_number = e.target.value;
    setFormData((p) => ({ ...p, mobile_number }));
    if (mobile_number.length >= 10) {
      try {
        // Ask the server by number; the guest list screen only loads one page of guests.
        const found = await lookupGuestByMobile(mobile_number);
        let response = null;
        if (!found) response = await getAllGuests({ pagination, hotel_id: hotel_id1 });
        const matched = found
          ? { first_name: found.name, guest_id: found.guest_id }
          : response?.data?.guests?.find((g) => g.mobile_number === mobile_number);
        if (matched) {
          const fullName = found ? found.name : [matched.first_name, matched.middle_name, matched.last_name].filter(Boolean).join(" ");
          setFormData((p) => ({ ...p, name: fullName, guest_id: matched.guest_id }));
          setIsNameEditable(false);
        } else {
          setFormData((p) => ({ ...p, name: "", guest_id: null }));
          setIsNameEditable(true);
        }
      } catch {
        setFormData((p) => ({ ...p, name: "", guest_id: null }));
        setIsNameEditable(true);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
     const selectedBillingType = formData.billing_type || "direct";

const appointmentData = {
  ...formData,
  service_id: formData.service_ids?.[0] || formData.service_id,
  service_ids: formData.service_ids || [formData.service_id],

  // ✅ Do not force reservation_id null for direct.
  // If direct SPA belongs to hotel guest stay, keep reservation link for final bill status display.
  reservation_id: formData.reservation_id || null,

  billing_type: selectedBillingType,
};
      if (editingAppointment) {
        await updateSpaAppointment({ ...appointmentData, id: editingAppointment.id });
        toast.success("Appointment updated successfully");
      } else {
        await addSpaAppointment(appointmentData);
        toast.success("Appointment added successfully");
      }
      fetchAppointments();
      handleCloseModal();
    } catch { toast.error("Failed to save appointment"); }
  };

  const handleDeleteClick = (appt) => { setAppointmentToDelete(appt); setShowDeleteConfirmModal(true); };
  const confirmDelete = async () => {
    try {
      await deleteSpaAppointment({ id: appointmentToDelete.id });
      toast.success("Appointment deleted successfully");
      fetchAppointments();
    } catch { toast.error("Failed to delete appointment"); }
    setShowDeleteConfirmModal(false); setAppointmentToDelete(null);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Billing modal open
  // ─────────────────────────────────────────────────────────────────────────

  const handleShowBillingModal = async (appointment) => {
    const serviceIds      = parseServiceIds(appointment);
    const selectedServices= serviceIds.map((sid) => services.find((s) => Number(s.id) === sid)).filter(Boolean);

    let calculatedSubtotal = parseFloat(
      selectedServices.reduce((s, svc) => s + (parseFloat(svc?.price) || 0), 0).toFixed(2)
    );

    // ── Defaults ──
    let existingDiscount      = 0;
    const appointmentBillingType = String(appointment.billing_type || "direct").toLowerCase();

let existingPaymentStatus =
  appointmentBillingType === "direct" ? "PAID" : "UNPAID";
    let existingGstMode       = "bill_level";
    let existingBillGstRate   = 0;

    const initialGstRates = {};
    selectedServices.forEach((svc) => { initialGstRates[svc.id] = parseFloat(svc.gst || 0); });

    const existingBilling = spaBillingData[appointment.id];
    if (existingBilling) {
  existingDiscount = existingBilling.discount || 0;
  existingPaymentStatus =
    existingBilling.payment_status ||
    (appointmentBillingType === "direct" ? "PAID" : "UNPAID");
      setExistingBillingId(existingBilling.id);

      // Restore GST mode from saved line_items metadata
      try {
        const meta = typeof existingBilling.line_items === "string"
          ? JSON.parse(existingBilling.line_items) : existingBilling.line_items;

        if (meta?.gst_mode) {
          existingGstMode     = meta.gst_mode;
          existingBillGstRate = meta.bill_gst_rate || 0;
        }
        if (Array.isArray(meta?.lines)) {
          meta.lines.forEach((l) => { initialGstRates[l.service_id] = l.gst_rate_percent || 0; });
        }
      } catch { /* keep defaults */ }
    }

    setGstMode(existingGstMode);
    setBillGstRate(existingBillGstRate);
    setEditableGstRates(initialGstRates);

    const { finalAmount, gstPercentage } = recalcFinalAmount(
      calculatedSubtotal, existingDiscount,
      existingGstMode, initialGstRates, existingBillGstRate, selectedServices
    );

    setBillingData({
      gst_percentage: gstPercentage,
      subtotal      : calculatedSubtotal,
      discount      : existingDiscount,
      final_amount  : existingBilling ? existingBilling.final_amount : finalAmount,
      payment_status: existingPaymentStatus,
      appointment,
    });

    setIsEditingBilling(!!existingBilling);
    setBillingStep(1);
    setShowBillingModal(true);
  };

  const handleCloseBillingModal = () => {
    setShowBillingModal(false);
    setBillingData({ gst_percentage: 0, subtotal: 0, discount: 0, final_amount: 0, payment_status: "UNPAID", appointment: null });
    setIsEditingBilling(false);
    setExistingBillingId(null);
    setEditableGstRates({});
    setGstMode("bill_level");
    setBillGstRate(0);
    setBillingStep(1);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Change handlers for GST
  // ─────────────────────────────────────────────────────────────────────────

  const handleGstModeChange = (newMode) => {
    setGstMode(newMode);
    const svcs = getSelectedServices();
    const { finalAmount, gstPercentage } = recalcFinalAmount(
      billingData.subtotal, billingData.discount,
      newMode, editableGstRates, billGstRate, svcs
    );
    setBillingData((p) => ({ ...p, final_amount: finalAmount, gst_percentage: gstPercentage }));
  };

  const handleBillGstRateChange = (val) => {
    const rate = parseFloat(val) || 0;
    setBillGstRate(rate);
    const svcs = getSelectedServices();
    const { finalAmount, gstPercentage } = recalcFinalAmount(
      billingData.subtotal, billingData.discount, "bill_level", editableGstRates, rate, svcs
    );
    setBillingData((p) => ({ ...p, final_amount: finalAmount, gst_percentage: gstPercentage }));
  };

  const handleGstRateChange = (svcId, newVal) => {
    const newGst     = parseFloat(newVal) || 0;
    const updatedRates = { ...editableGstRates, [svcId]: newGst };
    setEditableGstRates(updatedRates);
    const svcs = getSelectedServices();
    const { finalAmount, gstPercentage } = recalcFinalAmount(
      billingData.subtotal, billingData.discount, gstMode, updatedRates, billGstRate, svcs
    );
    setBillingData((p) => ({ ...p, final_amount: finalAmount, gst_percentage: gstPercentage }));
  };

  const handleDiscountChange = (e) => {
    const discount = parseFloat(e.target.value) || 0;
    if (discount > billingData.subtotal) { toast.warning("Discount cannot exceed base price"); return; }
    const svcs = getSelectedServices();
    const { finalAmount, gstPercentage } = recalcFinalAmount(
      billingData.subtotal, discount, gstMode, editableGstRates, billGstRate, svcs
    );
    setBillingData((p) => ({ ...p, discount, final_amount: finalAmount, gst_percentage: gstPercentage }));
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Create / Update billing
  // ─────────────────────────────────────────────────────────────────────────

const handleCreateBilling = async () => {
  try {
    const alreadyBilled = spaBillingData[billingData.appointment.id];
    const resolvedBillingId = existingBillingId || alreadyBilled?.id || null;
    const shouldUpdate = !!resolvedBillingId;

    if (shouldUpdate && !existingBillingId) {
      setExistingBillingId(alreadyBilled.id);
    }

    const rawBillingType = String(
      billingData.appointment.billing_type || "direct"
    ).toUpperCase();

    const billingType =
      rawBillingType === "ROOM" || rawBillingType === "CHARGE_TO_ROOM"
        ? "ROOM"
        : "DIRECT";

    const paymentStatus =
      billingType === "DIRECT"
        ? billingData.payment_status || "PAID"
        : billingData.payment_status || "UNPAID";

    const payload = {
      customer_id:
        billingData.appointment.Customer?.id ||
        billingData.appointment.customer_id,

      service_id: billingData.appointment.service_id,
      therapist_name: billingData.appointment.therapist_name,
      time: billingData.appointment.appointment_time,
      appointment_id: billingData.appointment.id,
      hotel_id: hotel_id1,

      // ✅ Keep reservation_id for both DIRECT and ROOM if it belongs to a stay.
      // Checkout will exclude DIRECT amount using billing_type.
      reservation_id: billingData.appointment.reservation_id || null,

      // ✅ New important field
      billing_type: billingType,

      payment_status: paymentStatus,
      discount: billingData.discount || 0,

      gst_mode: gstMode,
      bill_gst_rate: gstMode === "bill_level" ? billGstRate : 0,
      gst_overrides: gstMode === "per_service" ? editableGstRates : {},

      gst_percentage: billingData.gst_percentage,
      subtotal: billingData.subtotal,
      final_amount: billingData.final_amount,
    };

    let response;

    if (shouldUpdate) {
      response = await updateSpaBilling(resolvedBillingId, payload);
      if (response) toast.success("Spa billing updated successfully");
    } else {
      response = await createSpaBilling(payload);
      if (response) toast.success("Spa billing created successfully");
    }

    if (response) {
      const savedBilling = response.data;

      if (savedBilling) {
        setSpaBillingData((prev) => ({
          ...prev,
          [billingData.appointment.id]: savedBilling,
        }));

        setBillingData((prev) => ({
          ...prev,
          subtotal: savedBilling.subtotal,
          discount: savedBilling.discount,
          gst_percentage: savedBilling.gst_percentage,
          final_amount: savedBilling.final_amount,
          payment_status: savedBilling.payment_status,
        }));
      }

      setShowBillingModal(false);
      setExistingBillingId(null);
      setIsEditingBilling(false);

      await updateSpaAppointment({
        ...billingData.appointment,
        id: billingData.appointment.id,
        status: "COMPLETED",
        billing_type: billingType === "ROOM" ? "room" : "direct",
        reservation_id: billingData.appointment.reservation_id || null,
      });

      fetchAppointments();
    }
  } catch (error) {
    console.error("Error saving spa billing:", error);
    toast.error(`Failed to ${isEditingBilling ? "update" : "create"} spa billing`);
  }
};

  // ─────────────────────────────────────────────────────────────────────────
  // Invoice helpers
  // ─────────────────────────────────────────────────────────────────────────

  const generateInvoice = async (appointment) => {
    const billing = spaBillingData[appointment.id];
    if (!billing) { toast.error("Please create billing first."); return; }
    const appointmentServices = parseServiceIds(appointment)
      .map((sid) => services.find((s) => Number(s.id) === sid)).filter(Boolean);
    const hotelName = hotelDetails?.hotel_name || "Spa";
    const invoiceHtml = getInvoiceTemplate(
      appointment, appointmentServices, hotelName, hotelDetails,
      billing.gst_percentage, billing.subtotal, billing.final_amount,
      billing.discount, billing.invoice_number,
      billing.payment_status || "UNPAID",
      appointment.billing_type || "direct",
      billing.line_items
    );
    const blob = new Blob([invoiceHtml], { type: "text/html" });
    const url  = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${hotelName}-Spa-Invoice-${billing.invoice_number}.html`;
    document.body.appendChild(link); link.click();
    document.body.removeChild(link); window.URL.revokeObjectURL(url);
  };

  const handlePrint = (appointment) => {
    const billing = spaBillingData[appointment.id];
    if (!billing) { toast.error("Please create billing first."); return; }
    const appointmentServices = parseServiceIds(appointment)
      .map((sid) => services.find((s) => Number(s.id) === sid)).filter(Boolean);
    const hotelName = hotelDetails?.hotel_name || "Spa";
    const invoiceHtml = getInvoiceTemplate(
      appointment, appointmentServices, hotelName, hotelDetails,
      billing.gst_percentage, billing.subtotal, billing.final_amount,
      billing.discount, billing.invoice_number,
      billing.payment_status || "UNPAID",
      appointment.billing_type || "direct",
      billing.line_items
    );
    const w = window.open("", "_blank");
    w.document.write(invoiceHtml); w.document.close(); w.print();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Invoice template (unchanged from your original — supports new line_items
  // structure with gst_mode metadata at the top level)
  // ─────────────────────────────────────────────────────────────────────────

  const getInvoiceTemplate = (
    appointment, appointmentServices, hotelName, hotelDetails,
    gstPercentage, subtotal = null, customFinalAmount = null, discount = 0,
    invoiceNumber = null, paymentStatus = "UNPAID", billingType = "direct",
    rawLineItems = null
  ) => {
    const fmt = (num) =>
      parseFloat(num || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // ── Parse line_items — new format wraps lines inside { gst_mode, lines: [...] }
    const parsedMeta = (() => {
      try {
        if (!rawLineItems) return null;
        const obj = typeof rawLineItems === "string" ? JSON.parse(rawLineItems) : rawLineItems;
        // Support both old format (array) and new format ({ gst_mode, lines })
        if (Array.isArray(obj)) return { gst_mode: "bill_level", lines: obj };
        if (obj?.lines) return obj;
        return null;
      } catch { return null; }
    })();

    const lineItems = parsedMeta?.lines || null;
    const savedGstMode = parsedMeta?.gst_mode || "bill_level";
    const savedBillGstRate = parsedMeta?.bill_gst_rate || 0;

    const basePrice   = subtotal !== null ? parseFloat(subtotal) : 0;
    const discountAmt = parseFloat(discount) || 0;
    const netAmount   = parseFloat((basePrice - discountAmt).toFixed(2));
    const totalAmount = customFinalAmount !== null ? parseFloat(customFinalAmount) : netAmount;

    let totalGstAmount;
    if (savedGstMode === "bill_level") {
      totalGstAmount = parseFloat((netAmount * (savedBillGstRate / 100)).toFixed(2));
    } else {
      totalGstAmount = lineItems
        ? parseFloat(lineItems.reduce((s, l) => s + (l.line_gst || 0), 0).toFixed(2))
        : parseFloat(((basePrice - discountAmt) * ((gstPercentage || 0) / 100)).toFixed(2));
    }

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>${hotelName} - Spa Invoice</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: A4 portrait; margin: 15mm 12mm; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 11px; color: #2d2d2d; background: #fff; width: 210mm; margin: 0 auto; padding: 10mm 12mm; }

    .hotel-header { text-align: center; padding-bottom: 12px; margin-bottom: 6px; border-bottom: 3px solid #1a237e; }
    .hotel-header img { display: block; margin: 0 auto 8px; max-width: 100px; max-height: 65px; object-fit: contain; }
    .hotel-name { font-size: 18px; font-weight: 800; color: #1a237e; text-transform: uppercase; letter-spacing: 2px; }
    .hotel-address { text-align: center; font-size: 9.5px; margin: 5px 0 8px; color: #666; line-height: 1.8; }

    .invoice-header { text-align: center; margin: 10px 0 8px; }
    .invoice-header h2 { font-size: 13px; font-weight: 700; color: #37474f; text-transform: uppercase; letter-spacing: 2px; }
    .invoice-header h2:after { content: ''; display: block; width: 40px; height: 2.5px; background: #1a237e; margin: 5px auto 0; border-radius: 2px; }
    .inv-num { display: inline-block; font-size: 11px; font-weight: 700; color: #1a237e; background: #e8eaf6; padding: 4px 14px; border-radius: 20px; margin: 5px 0 2px; }
    .inv-date { font-size: 10px; color: #888; margin-top: 2px; }
    .pay-status { display: inline-block; padding: 3px 12px; border-radius: 20px; font-weight: 700; font-size: 10px; margin-top: 5px; text-transform: uppercase; }
    .s-paid    { background: #e8f5e9; color: #1b5e20; border: 1.5px solid #43a047; }
    .s-unpaid  { background: #fff3e0; color: #bf360c; border: 1.5px solid #fb8c00; }
    .s-pending { background: #fff8e1; color: #f57f17; border: 1.5px solid #fdd835; }

    .gst-mode-tag { display:inline-block; margin-left:8px; padding:2px 10px; border-radius:12px; font-size:9.5px; font-weight:600; }
    .tag-bill  { background:#e3f2fd; color:#1565c0; border:1px solid #90caf9; }
    .tag-item  { background:#f3e5f5; color:#6a1b9a; border:1px solid #ce93d8; }

    .guest-box { margin: 10px 0 8px; padding: 10px 14px; background: #f5f7ff; border-radius: 6px; border-left: 4px solid #3f51b5; font-size: 10.5px; display: flex; gap: 30px; flex-wrap: wrap; }
    .guest-box p { margin: 2px 0; line-height: 1.6; }

    .services-table { width: 100%; border-collapse: collapse; margin: 10px 0 0; font-size: 10px; }
    .services-table thead tr { background: #263238; color: #fff; }
    .services-table th { padding: 8px 9px; font-size: 9.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.6px; }
    .services-table td { padding: 7px 9px; border-bottom: 1px solid #eeeeee; }

    .summary-wrapper { margin-top: 12px; display: flex; justify-content: flex-end; }
    .summary-box { width: 300px; border: 1px solid #e0e0e0; border-radius: 6px; overflow: hidden; }
    .summary-box table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
    .summary-box td { padding: 6px 12px; border-bottom: 1px solid #f0f0f0; }
    .summary-box td:last-child { text-align: right; }
    .summary-box .label { color: #666; }
    .summary-box .value { font-weight: 600; }
    .summary-box .grand-total td { background: #1a237e; color: #fff; font-size: 13px; font-weight: 700; padding: 9px 12px; }

    .gst-details { margin-top: 10px; font-size: 9.5px; color: #555; background: #f5f5f5; padding: 9px 12px; border-radius: 5px; border-left: 3px solid #90a4ae; }
    .gst-details p { margin: 3px 0; line-height: 1.5; }

    .footer { margin-top: 14px; text-align: center; font-size: 9.5px; color: #9e9e9e; padding-top: 10px; border-top: 1px dashed #e0e0e0; }
    @media print { body { width: 100% !important; padding: 5mm !important; } }
  </style>
</head>
<body>
<div class="bill-container">

  <div class="hotel-header">
    ${hotelDetails?.hotel_image ? `<img src="${BASE_URL_for_images}/${hotelDetails.hotel_image}" alt="${hotelName}" />` : ""}
    <div class="hotel-name">${hotelName}</div>
  </div>

  <div class="hotel-address">
    ${[hotelDetails?.address, hotelDetails?.city, hotelDetails?.state].filter(Boolean).join(", ")}
    ${hotelDetails?.gst_number && hotelDetails.gst_number !== "null" ? `<br/>GST No: ${hotelDetails.gst_number}` : ""}
    ${hotelDetails?.hotel_mobile || hotelDetails?.owner?.mobile ? `<br/>📞 ${hotelDetails.hotel_mobile || hotelDetails.owner?.mobile}` : ""}
    ${hotelDetails?.hotel_email  || hotelDetails?.owner?.email  ? `<br/>✉ ${hotelDetails.hotel_email  || hotelDetails.owner?.email}` : ""}
  </div>

  <div class="invoice-header">
    <h2>SPA SERVICE INVOICE</h2>
    <div class="inv-num">Invoice No: ${invoiceNumber}</div>
    <div class="inv-date">Date: ${new Date(appointment.appointment_time).toLocaleDateString("en-GB")}</div>
    <div>
      <span class="pay-status ${paymentStatus === "PAID" ? "s-paid" : paymentStatus === "PENDING" ? "s-pending" : "s-unpaid"}">
        ${paymentStatus === "PAID" ? "✓ PAID" : paymentStatus === "PENDING" ? "⏳ PENDING" : "✗ UNPAID"}
      </span>
      <span class="gst-mode-tag ${savedGstMode === "bill_level" ? "tag-bill" : "tag-item"}">
        GST: ${savedGstMode === "bill_level" ? `Bill Level @${savedBillGstRate}%` : "Per Service"}
      </span>
      ${billingType === "room" ? `<span style="margin-left:8px;font-size:10px;color:#555;">Charged to Room</span>` : ""}
    </div>
  </div>

  <div class="guest-box">
    <p><strong>Guest:</strong> ${appointment.name}</p>
    <p><strong>Mobile:</strong> ${appointment.mobile_number}</p>
    <p><strong>Appointment:</strong> ${new Date(appointment.appointment_time).toLocaleString("en-GB", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })}</p>
  </div>

  <table class="services-table">
    <thead>
      <tr>
        <th style="text-align:left;width:25%;">Service</th>
        <th style="text-align:left;">Therapist</th>
        <th style="text-align:center;">Duration</th>
        <th style="text-align:right;">Base Price</th>
        <th style="text-align:right;">Discount</th>
        <th style="text-align:right;">Net Amt</th>
        ${savedGstMode === "bill_level" ? `<th style="text-align:center;">GST</th><th style="text-align:right;">Total</th>` : `<th style="text-align:right;">Line Total</th>`}
      </tr>
    </thead>
    <tbody>
      ${(() => {
        if (lineItems && lineItems.length > 0) {
          return lineItems.map((line) => {
            const svc = appointmentServices.find((s) => s.id === line.service_id);
            if (savedGstMode === "bill_level") {
              return `<tr>
                <td><strong>${line.service_name}</strong></td>
                <td>${appointment.therapist_name}</td>
                <td style="text-align:center;">${svc?.duration || 60} mins</td>
                <td style="text-align:right;">₹${fmt(line.line_amount)}</td>
                <td style="text-align:right;color:#e65100;">${line.line_discount > 0 ? `- ₹${fmt(line.line_discount)}` : "—"}</td>
                <td style="text-align:right;">₹${fmt(line.net_line_amount)}</td>
                <td style="text-align:right;font-weight:600;">₹${fmt(line.net_line_amount)}</td>
              </tr>`;
            }
            return `<tr>
              <td><strong>${line.service_name}</strong></td>
              <td>${appointment.therapist_name}</td>
              <td style="text-align:center;">${svc?.duration || 60} mins</td>
              <td style="text-align:right;">₹${fmt(line.line_amount)}</td>
              <td style="text-align:right;color:#e65100;">${line.line_discount > 0 ? `- ₹${fmt(line.line_discount)}` : "—"}</td>
              <td style="text-align:right;">₹${fmt(line.net_line_amount)}</td>
              <td style="text-align:center;color:${line.gst_rate_percent > 0 ? "#1a7a3c" : "#999"};">
                ${line.gst_rate_percent > 0 ? `${line.gst_rate_percent}%<br/><span style="font-size:9px;">₹${fmt(line.line_gst)}</span>` : "N/A"}
              </td>
              <td style="text-align:right;font-weight:600;">₹${fmt(line.line_total)}</td>
            </tr>`;
          }).join("");
        }
        // Fallback — no line items saved
        return appointmentServices.map((svc) => {
          const price = parseFloat(svc?.price) || 0;
          const gRate = parseFloat(svc?.gst || 0);
          const gAmt  = parseFloat(((price * gRate) / 100).toFixed(2));
          return `<tr>
            <td><strong>${svc?.service_name || ""}</strong></td>
            <td>${appointment.therapist_name}</td>
            <td style="text-align:center;">${svc?.duration || 60} mins</td>
            <td style="text-align:right;">₹${fmt(price)}</td>
            <td style="text-align:right;">—</td>
            <td style="text-align:right;">₹${fmt(price)}</td>
            <td style="text-align:center;">${gRate > 0 ? `${gRate}%<br/><span style="font-size:9px;">₹${fmt(gAmt)}</span>` : "N/A"}</td>
            <td style="text-align:right;">₹${fmt(price + gAmt)}</td>
          </tr>`;
        }).join("");
      })()}

      ${savedGstMode === "bill_level" ? `
      <tr style="background:#e3f2fd;">
        <td colspan="5" style="padding:7px 9px;font-weight:600;color:#1565c0;">
          Bill-Level GST @ ${savedBillGstRate}%
        </td>
        <td style="text-align:right;font-weight:700;color:#1565c0;">₹${fmt(totalGstAmount)}</td>
        <td style="text-align:right;font-weight:700;color:#1565c0;">+GST</td>
      </tr>` : ""}
    </tbody>
  </table>

  <div class="summary-wrapper">
    <div class="summary-box">
      <table>
        <tr><td class="label">Base Amount</td><td class="value">₹${fmt(basePrice)}</td></tr>
        <tr><td class="label">Discount</td><td class="value" style="color:#e65100;">− ₹${fmt(discountAmt)}</td></tr>
        <tr><td class="label">Net Amount</td><td class="value">₹${fmt(netAmount)}</td></tr>
        ${totalGstAmount > 0
          ? `<tr><td class="label">${savedGstMode === "bill_level" ? `Bill GST (${savedBillGstRate}%)` : `GST (${gstPercentage}% avg)`}</td>
             <td class="value" style="color:#1b5e20;">₹${fmt(totalGstAmount)}</td></tr>`
          : `<tr><td class="label" style="color:#999;">GST</td><td style="text-align:right;color:#999;">Not Applicable</td></tr>`}
        <tr class="grand-total"><td>Grand Total</td><td>₹${fmt(totalAmount)}</td></tr>
      </table>
    </div>
  </div>

  ${totalGstAmount > 0 ? `
  <div class="gst-details">
    ${savedGstMode === "bill_level"
      ? `<p><strong>Bill-Level GST @ ${savedBillGstRate}%</strong> applied on net amount ₹${fmt(netAmount)}</p>
         <p>CGST ${savedBillGstRate / 2}% = ₹${fmt(totalGstAmount / 2)} &nbsp;|&nbsp; SGST ${savedBillGstRate / 2}% = ₹${fmt(totalGstAmount / 2)}</p>`
      : `<p><strong>GST Breakup (per service):</strong></p>
         ${lineItems
           ? lineItems.filter((l) => l.line_gst > 0).map((l) =>
               `<p style="display:flex;justify-content:space-between;">
                  <span>${l.service_name} @ ${l.gst_rate_percent}%</span>
                  <span>CGST ${l.gst_rate_percent/2}% = ₹${fmt(l.line_gst/2)} | SGST ${l.gst_rate_percent/2}% = ₹${fmt(l.line_gst/2)}</span>
                </p>`).join("")
           : `<p>CGST: ₹${fmt(totalGstAmount/2)} | SGST: ₹${fmt(totalGstAmount/2)}</p>`}
         <p style="border-top:1px solid #e0e0e0;margin-top:5px;padding-top:5px;"><strong>Total GST: ₹${fmt(totalGstAmount)}</strong></p>`
    }
  </div>` : `<div class="gst-details" style="color:#999;"><p>No GST applicable on this bill.</p></div>`}

  <div class="footer">
    <p>Thank you for choosing ${hotelName}</p>
    <p>We look forward to serving you again!</p>
    <small>This is a computer-generated invoice</small>
  </div>
</div>
</body>
</html>`;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Billing modal — Step 2 GST section (with mode toggle)
  // ─────────────────────────────────────────────────────────────────────────

  const renderStep2Gst = () => {
    const selectedServices = getSelectedServices();

    return (
      <div>
        <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "8px", color: "#333" }}>
          Step 2 — Set GST Method &amp; Rate
        </div>

        {/* ── Mode toggle ── */}
        <div style={{
          display: "flex", gap: "10px", marginBottom: "14px",
          background: "#f8f9ff", border: "1px solid #c5cae9",
          borderRadius: "6px", padding: "10px 12px",
        }}>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "#333", alignSelf: "center", minWidth: 80 }}>
            GST Method:
          </div>
          {[
            // { val: "per_service", label: "Per Service", icon: "📋", desc: "Different rate per service" },
            { val: "bill_level",  label: "Bill Level",  icon: "🧾", desc: "One rate on entire bill" },
          ].map(({ val, label, icon, desc }) => (
            <button
              key={val}
              type="button"
              onClick={() => handleGstModeChange(val)}
              style={{
                flex: 1, padding: "8px 12px", borderRadius: "6px", cursor: "pointer",
                border: gstMode === val ? "2px solid #3f51b5" : "1.5px solid #ddd",
                background: gstMode === val ? "#e8eaf6" : "#fff",
                color: gstMode === val ? "#1a237e" : "#555",
                fontWeight: gstMode === val ? 700 : 400,
                fontSize: "12px", textAlign: "left", transition: "all 0.15s",
              }}
            >
              <div style={{ fontSize: "15px", marginBottom: "2px" }}>{icon} {label}</div>
              <div style={{ fontSize: "10px", color: gstMode === val ? "#3949ab" : "#888" }}>{desc}</div>
            </button>
          ))}
        </div>

        {/* ── Bill-level: single rate input ── */}
        {gstMode === "bill_level" && (
          <div style={{
            background: "#e3f2fd", border: "1px solid #90caf9",
            borderRadius: "6px", padding: "12px 16px", marginBottom: "12px",
          }}>
            <div style={{ fontWeight: 600, fontSize: "12px", color: "#1565c0", marginBottom: "8px" }}>
              🧾 Bill-Level GST Rate
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: "11px", color: "#555", marginBottom: "4px", display: "block" }}>
                  GST % applied on entire net bill (after discount)
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="number" min="0" max="100" step="0.5"
                    value={billGstRate}
                    onChange={(e) => handleBillGstRateChange(e.target.value)}
                    style={{
                      width: "90px", padding: "6px 10px", fontSize: "16px", fontWeight: 700,
                      border: "2px solid #1565c0", borderRadius: "6px",
                      background: "#fff", textAlign: "center", color: "#1565c0",
                    }}
                  />
                  <span style={{ fontSize: "14px", fontWeight: 700, color: "#1565c0" }}>%</span>
                  {/* Quick-select buttons */}
                  <div style={{ display: "flex", gap: "5px" }}>
                    {[0, 5, 12, 18, 28].map((pct) => (
                      <button
                        key={pct}
                        type="button"
                        onClick={() => handleBillGstRateChange(pct)}
                        style={{
                          padding: "4px 8px", borderRadius: "4px", fontSize: "11px",
                          border: billGstRate === pct ? "2px solid #1565c0" : "1px solid #90caf9",
                          background: billGstRate === pct ? "#1565c0" : "#fff",
                          color: billGstRate === pct ? "#fff" : "#1565c0",
                          cursor: "pointer", fontWeight: billGstRate === pct ? 700 : 400,
                        }}
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Live preview */}
            {billGstRate > 0 && (
              <div style={{
                marginTop: "10px", fontSize: "11px", padding: "8px 10px",
                background: "#fff", borderRadius: "4px", border: "1px solid #90caf9",
                display: "flex", gap: "20px",
              }}>
                <span>Net after discount: <strong>₹{Number((billingData.subtotal || 0) - (billingData.discount || 0)).toFixed(2)}</strong></span>
                <span>GST ({billGstRate}%): <strong style={{ color: "#1a7a3c" }}>
                  ₹{Number(((billingData.subtotal || 0) - (billingData.discount || 0)) * (billGstRate || 0) / 100).toFixed(2)}
                </strong></span>
                <span>Total: <strong style={{ color: "#1a237e" }}>₹{Number(billingData.final_amount || 0).toFixed(2)}</strong></span>
              </div>
            )}
          </div>
        )}

        {/* ── Per-service: editable table ── */}
        {gstMode === "per_service" && (
          <div>
            <div style={{
              fontSize: "11px", color: "#3949ab", background: "#f0f4ff",
              border: "1px solid #c5cae9", borderRadius: "4px", padding: "6px 10px", marginBottom: "10px",
            }}>
              ℹ Enter GST % per service. Use 0 for non-GST items. GST applies on net amount after proportional discount.
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ background: "#1e1e1e", color: "#fff" }}>
                  <th style={{ padding: "7px 10px", textAlign: "left" }}>Service</th>
                  <th style={{ padding: "7px 10px", textAlign: "right" }}>Net Amt</th>
                  <th style={{ padding: "7px 10px", textAlign: "center", width: "110px" }}>GST Rate (%)</th>
                  <th style={{ padding: "7px 10px", textAlign: "right" }}>GST Amount</th>
                  <th style={{ padding: "7px 10px", textAlign: "right" }}>Line Total</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let grandNet = 0, grandGst = 0, grandTotal = 0;
                  const rows = selectedServices.map((svc) => {
                    const price      = parseFloat(svc.price) || 0;
                    const lineDisc   = billingData.subtotal > 0
                      ? parseFloat(((price / billingData.subtotal) * billingData.discount).toFixed(2)) : 0;
                    const netLine    = parseFloat((price - lineDisc).toFixed(2));
                    const gstRate    = editableGstRates[svc.id] ?? parseFloat(svc.gst || 0);
                    const gstAmt     = parseFloat((netLine * gstRate / 100).toFixed(2));
                    const lineTotal  = parseFloat((netLine + gstAmt).toFixed(2));
                    grandNet   += netLine;
                    grandGst   += gstAmt;
                    grandTotal += lineTotal;
                    return (
                      <tr key={svc.id} style={{ borderBottom: "1px solid #e0e0e0" }}>
                        <td style={{ padding: "8px 10px" }}>{svc.service_name}</td>
                        <td style={{ padding: "8px 10px", textAlign: "right" }}>
                          <span>₹{Number(netLine || 0).toFixed(2)}</span>
                          {lineDisc > 0 && <span style={{ display: "block", fontSize: "9px", color: "#e65100" }}>(- ₹{Number(lineDisc || 0).toFixed(2)})</span>}
                        </td>
                        <td style={{ padding: "8px 10px", textAlign: "center" }}>
                          <input
                            type="number" min="0" max="100" step="0.01"
                            value={editableGstRates[svc.id] ?? parseFloat(svc.gst || 0)}
                            onChange={(e) => handleGstRateChange(svc.id, e.target.value)}
                            style={{
                              width: "75px", padding: "4px 6px",
                              border: "1px solid #3f51b5", borderRadius: "4px",
                              fontSize: "13px", textAlign: "center", background: "#f8f9ff",
                            }}
                          />
                          <span style={{ fontSize: "10px", color: "#888", display: "block" }}>% GST</span>
                        </td>
                        <td style={{ padding: "8px 10px", textAlign: "right" }}>
                          {gstAmt > 0 ? <span style={{ color: "#1a7a3c" }}>₹{gstAmt.toFixed(2)}</span> : <span style={{ color: "#999" }}>—</span>}
                        </td>
                        <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600 }}>₹{lineTotal.toFixed(2)}</td>
                      </tr>
                    );
                  });
                  return (
                    <>
                      {rows}
                      <tr style={{ background: "#f0fff4", fontWeight: 700, borderTop: "2px solid #1a7a3c" }}>
                        <td style={{ padding: "7px 10px" }}>Total</td>
                        <td style={{ padding: "7px 10px", textAlign: "right" }}>₹{grandNet.toFixed(2)}</td>
                        <td style={{ padding: "7px 10px", textAlign: "center", color: "#999", fontSize: "11px" }}>weighted</td>
                        <td style={{ padding: "7px 10px", textAlign: "right", color: "#1a7a3c" }}>₹{grandGst.toFixed(2)}</td>
                        <td style={{ padding: "7px 10px", textAlign: "right" }}>₹{grandTotal.toFixed(2)}</td>
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Spa Appointments" breadcrumbItem="Manage Appointments" />

        <div className="d-flex flex-column flex-md-row gap-2 mb-3">
          <Button variant="primary" onClick={() => handleShowModal()} className="w-100 w-md-auto">Add New Appointment</Button>
          <Button variant="warning" onClick={() => navigation("/spaService")} className="w-100 w-md-auto">Add service</Button>
          <Button variant="secondary" onClick={() => navigation("/dashboard")} className="w-100 w-md-auto">Back</Button>
        </div>

        <SpaAppointmentExcelExport hotelId={hotel_id1} onExportComplete={() => {}} />

        <div className="table-responsive">
          <Table>
            <thead>
              <tr>
                <th>Sr.</th>
                <th className="d-none d-md-table-cell">Mobile</th>
                <th>Name</th>
                <th className="d-none d-lg-table-cell">Service</th>
                <th className="d-none d-md-table-cell">Therapist</th>
                <th className="d-none d-lg-table-cell">Price</th>
                <th className="d-none d-lg-table-cell">Date &amp; Time</th>
                <th className="d-none d-md-table-cell">Status</th>
                {/* ✅ Payment status column */}
                <th className="d-none d-md-table-cell">Payment</th>
                <th>Actions</th>
                <th className="d-md-table-cell">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {appointments?.map((appointment, index) => (
                <tr key={appointment.id}>
                  <td>{(paginationInfo.currentPage - 1) * pagination.limit + index + 1}</td>
                  <td className="d-none d-md-table-cell">{appointment.mobile_number}</td>
                  <td>
                    <div className="d-flex flex-column">
                      <span>{appointment.name}</span>
                      <small className="text-muted d-md-none">{appointment.mobile_number}</small>
                      {/* ✅ Mobile: show payment badge inline under name */}
                      <div className="d-md-none mt-1">
                        <PaymentStatusBadge status={spaBillingData[appointment.id]?.payment_status} />
                      </div>
                    </div>
                  </td>
                  <td className="d-none d-lg-table-cell">
                    {parseServiceIds(appointment)
                      .map((sid) => services?.find((s) => Number(s.id) === sid)?.service_name)
                      .filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="d-none d-md-table-cell">{appointment.therapist_name}</td>
                <td className="d-none d-lg-table-cell">
  {(() => {
    const billing = spaBillingData[appointment.id];
    if (billing?.final_amount) {
      // Has billing — show actual billed amount with GST/discount applied
      return (
        <span>
          ₹{parseFloat(billing.final_amount).toFixed(2)}
          {billing.discount > 0 && (
            <small style={{ display: "block", color: "#e65100", fontSize: "10px" }}>
              (disc: ₹{parseFloat(billing.discount).toFixed(2)})
            </small>
          )}
          {billing.gst_percentage > 0 && (
            <small style={{ display: "block", color: "#1a7a3c", fontSize: "10px" }}>
              (GST {billing.gst_percentage}% incl.)
            </small>
          )}
        </span>
      );
    }
    // No billing yet — show raw base price sum
    const basePrice = parseServiceIds(appointment).reduce((sum, sid) => {
      const svc = services?.find((s) => Number(s.id) === sid);
      return sum + (parseFloat(svc?.price) || 0);
    }, 0);
    return <span style={{ color: "#888" }}>₹{Number(basePrice || 0).toFixed(2)}</span>;
  })()}
</td>
                  <td className="d-none d-lg-table-cell">
                    {new moment(appointment.appointment_time).format("DD-MM-YYYY - HH:mm")}
                  </td>
                  <td className="d-none d-md-table-cell">
                    <SpaAppointmentStatusBadge status={appointment.status} />
                  </td>
                  {/* ✅ Payment status — desktop */}
                  <td className="d-none d-md-table-cell">
                    <PaymentStatusBadge status={spaBillingData[appointment.id]?.payment_status} />
                  </td>
                  <td>
                    <div className="d-flex gap-1 justify-content-center">
                      <Button variant="info"    size="sm" onClick={() => handleShowModal(appointment)}><i className="mdi mdi-pencil" /></Button>
                      <Button variant="danger"  size="sm" onClick={() => handleDeleteClick(appointment)}><i className="mdi mdi-delete" /></Button>
                      <Button variant="success" size="sm" onClick={() => handleShowBillingModal(appointment)}>Billing</Button>
                    </div>
                  </td>
                  <td className="text-center d-md-table-cell">
                    {appointment.status === "COMPLETED" && spaBillingData[appointment.id] && (
                      <div className="d-flex">
                        <Button variant="link" className="me-0" onClick={() => generateInvoice(appointment)} title="Download Invoice">
                          <i className="fas fa-download" />
                        </Button>
                        <Button variant="link" onClick={() => handlePrint(appointment)} title="Print Invoice">
                          <i className="fas fa-print" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-end mt-3">
          <Pagination>
            <PaginationItem disabled={paginationInfo.currentPage <= 1}>
              <PaginationLink previous href="#" onClick={() => handlePageChange(paginationInfo.currentPage - 1)} />
            </PaginationItem>
            {[...Array(paginationInfo.totalPages || 0).keys()].map((n) => (
              <PaginationItem active={paginationInfo.currentPage === n + 1} key={n}>
                <PaginationLink href="#" onClick={() => handlePageChange(n + 1)}>{n + 1}</PaginationLink>
              </PaginationItem>
            ))}
            <PaginationItem disabled={paginationInfo.currentPage >= paginationInfo.totalPages}>
              <PaginationLink next href="#" onClick={() => handlePageChange(paginationInfo.currentPage + 1)} />
            </PaginationItem>
          </Pagination>
        </div>

        {/* ── Add/Edit Appointment Modal ── */}
        <Modal show={showModal} onHide={handleCloseModal} className="modal-fullscreen-md-down">
          <Modal.Header closeButton>
            <Modal.Title>{editingAppointment ? "Edit Appointment" : "Add New Appointment"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Mobile Number</Form.Label>
                <Form.Control type="text" name="mobile_number" value={formData.mobile_number || ""} onChange={handleMobileNumberChange} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control type="text" name="name" value={formData.name || ""} onChange={handleInputChange} disabled={!isNameEditable} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Service(s)</Form.Label>
                <Select isMulti
                  options={services?.map((s) => ({ value: s.id, label: `${s.service_name} (₹${s.price})` }))}
                  value={services.filter((s) => (formData.service_ids || [formData.service_id]).includes(s.id))
                    .map((s) => ({ value: s.id, label: `${s.service_name} (₹${s.price})` }))}
                  onChange={handleServiceChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Therapist Name</Form.Label>
                <Form.Control type="text" name="therapist_name" value={formData.therapist_name || ""} onChange={handleInputChange} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Appointment Time</Form.Label>
                <Form.Control type="datetime-local" name="appointment_time"
                  value={formData.appointment_time
                    ? new Date(formData.appointment_time).toLocaleString("sv-SE", { year:"numeric", month:"2-digit", day:"2-digit", hour:"2-digit", minute:"2-digit" }).replace(" ", "T")
                    : ""}
                  onChange={(e) => setFormData((p) => ({ ...p, appointment_time: new Date(e.target.value).toISOString() }))}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select name="status" value={formData.status || ""} onChange={handleInputChange} required>
                  <option value="PENDING">Pending</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Billing Type</Form.Label>
                <Form.Select name="billing_type" value={formData.billing_type || "direct"} onChange={handleInputChange} required>
                  <option value="direct">Direct Billing (Walk-in)</option>
                  <option value="room">Charge to Room</option>
                </Form.Select>
              </Form.Group>
              {formData.billing_type === "room" && (
                <Form.Group className="mb-3">
                  <Form.Label>Select Room</Form.Label>
                  <div className="d-flex gap-2">
                    <Button variant="outline-primary" onClick={() => { fetchAvailableReservations(); setShowReservationLookup(!showReservationLookup); }} className="w-100">
                      <i className="fas fa-search me-2" />
                      {formData.reservation_id ? "Room Selected" : "Search by Room Number"}
                    </Button>
                  </div>
                  {showReservationLookup && (
                    <div className="mt-2">
                      <Select
                        options={roomOptions(availableReservations)}
                        onChange={(sel) => { setFormData((p) => ({ ...p, reservation_id: sel.value })); setShowReservationLookup(false); }}
                        placeholder="Type room number to search..."
                      noOptionsMessage={() => "No checked-in room found"}
                        isSearchable isClearable
                      />
                    </div>
                  )}
                  <Form.Text className="text-muted">This appointment will be charged to the room reservation</Form.Text>
                </Form.Group>
              )}
              <Button variant="primary" type="submit">Save</Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* ── Delete Confirmation ── */}
        <Modal show={showDeleteConfirmModal} onHide={() => setShowDeleteConfirmModal(false)}>
          <Modal.Header closeButton><Modal.Title>Confirm Deletion</Modal.Title></Modal.Header>
          <Modal.Body>Are you sure you want to delete this appointment?</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteConfirmModal(false)}>Cancel</Button>
            <Button variant="danger"    onClick={confirmDelete}>Delete</Button>
          </Modal.Footer>
        </Modal>

        {/* ── BILLING MODAL ── */}
        <Modal show={showBillingModal} onHide={handleCloseBillingModal} size="lg" className="modal-fullscreen-md-down">
          <Modal.Header closeButton>
            <Modal.Title>
              {isEditingBilling ? "Edit Spa Billing" : "Create Spa Billing"}
              &nbsp;<small style={{ fontSize: "12px", color: "#888", fontWeight: 400 }}>Step {billingStep} of 3</small>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {billingData.appointment && (
              <>
                {/* Stepper */}
                <div className="d-flex align-items-center mb-4">
                  {[{ n: 1, label: "Discount" }, { n: 2, label: "GST" }, { n: 3, label: "Payment" }].map((s, i) => (
                    <React.Fragment key={s.n}>
                      <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: "50%",
                          background: billingStep >= s.n ? "#1a7a3c" : "#e0e0e0",
                          color: billingStep >= s.n ? "#fff" : "#888",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 700, fontSize: 14,
                          cursor: billingStep > s.n ? "pointer" : "default",
                        }} onClick={() => { if (billingStep > s.n) setBillingStep(s.n); }}>
                          {billingStep > s.n ? "✓" : s.n}
                        </div>
                        <small style={{ marginTop: 4, fontSize: 10, color: billingStep >= s.n ? "#1a7a3c" : "#888", fontWeight: billingStep === s.n ? 700 : 400 }}>
                          {s.label}
                        </small>
                      </div>
                      {i < 2 && (
                        <div style={{ flex: 2, height: 2, background: billingStep > s.n ? "#1a7a3c" : "#e0e0e0", marginBottom: 20 }} />
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {/* Guest / Service info — always visible */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <Form.Group><Form.Label>Guest Name</Form.Label>
                      <Form.Control type="text" value={billingData.appointment.name || ""} disabled />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group><Form.Label>Mobile Number</Form.Label>
                      <Form.Control type="text" value={billingData.appointment.mobile_number || ""} disabled />
                    </Form.Group>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <Form.Group><Form.Label>Service(s)</Form.Label>
                      <Form.Control type="text"
                        value={parseServiceIds(billingData.appointment)
                          .map((sid) => services.find((s) => Number(s.id) === sid)?.service_name).filter(Boolean).join(", ")}
                        disabled />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group><Form.Label>Therapist</Form.Label>
                      <Form.Control type="text" value={billingData.appointment.therapist_name || ""} disabled />
                    </Form.Group>
                  </div>
                </div>

                {/* Step 1 — Discount */}
                {billingStep === 1 && (
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "8px", color: "#333" }}>
                      Step 1 — Apply Discount (optional)
                    </div>
                    <div className="row">
                      <div className="col-md-4">
                        <Form.Group className="mb-3">
                          <Form.Label>Base Price (₹)</Form.Label>
                          <Form.Control type="number" min="0" value={billingData.subtotal}
                            onChange={(e) => {
                              const subtotal = parseFloat(e.target.value) || 0;
                              const { finalAmount, gstPercentage } = recalcFinalAmount(subtotal, billingData.discount, gstMode, editableGstRates, billGstRate, getSelectedServices());
                              setBillingData((p) => ({ ...p, subtotal, final_amount: finalAmount, gst_percentage: gstPercentage }));
                            }}
                          />
                        </Form.Group>
                      </div>
                      <div className="col-md-4">
                        <Form.Group className="mb-3">
                          <Form.Label>Discount (₹)</Form.Label>
                          <Form.Control type="number" min="0" max={billingData.subtotal} value={billingData.discount} onChange={handleDiscountChange} />
                          <Form.Text className="text-muted" style={{ fontSize: "11px" }}>Split proportionally per service</Form.Text>
                        </Form.Group>
                      </div>
                      <div className="col-md-4">
                        <Form.Group className="mb-3">
                          <Form.Label>Final Amount (₹)</Form.Label>
                          <Form.Control type="number" value={Number(billingData.final_amount || 0).toFixed(2)} readOnly
                            style={{ fontWeight: "bold", background: "#f0fff4", fontSize: "15px" }} />
                          <Form.Text className="text-muted" style={{ fontSize: "11px" }}>After discount + GST</Form.Text>
                        </Form.Group>
                      </div>
                    </div>
                    {/* Discount breakdown */}
                    {billingData.discount > 0 && (() => {
                      const svcs = getSelectedServices();
                      return (
                        <div style={{ fontSize: "12px", background: "#fff8e1", border: "1px solid #ffe082", borderRadius: "4px", padding: "8px 12px" }}>
                          <strong>Proportional discount breakdown:</strong>
                          <table style={{ width: "100%", marginTop: "5px", fontSize: "11px" }}>
                            <thead>
                              <tr style={{ color: "#666" }}>
                                <th style={{ textAlign: "left", paddingBottom: "3px" }}>Service</th>
                                <th style={{ textAlign: "right" }}>Base</th>
                                <th style={{ textAlign: "right" }}>Discount Share</th>
                                <th style={{ textAlign: "right" }}>Net</th>
                              </tr>
                            </thead>
                            <tbody>
                              {svcs.map((svc) => {
                                const price    = parseFloat(svc.price) || 0;
                                const lineDisc = billingData.subtotal > 0
                                  ? parseFloat(((price / billingData.subtotal) * billingData.discount).toFixed(2)) : 0;
                                const netLine  = parseFloat((price - lineDisc).toFixed(2));
                                return (
                                  <tr key={svc.id} style={{ borderTop: "1px solid #ffe082" }}>
                                    <td style={{ padding: "3px 0" }}>{svc.service_name}</td>
                                    <td style={{ textAlign: "right" }}>₹{Number(price || 0).toFixed(2)}</td>
                                    <td style={{ textAlign: "right", color: "#e65100" }}>- ₹{Number(lineDisc || 0).toFixed(2)}</td>
                                    <td style={{ textAlign: "right" }}>₹{Number(netLine || 0).toFixed(2)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Step 2 — GST */}
                {billingStep === 2 && renderStep2Gst()}

                {/* Step 3 — Payment */}
                {billingStep === 3 && (
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "13px", marginBottom: "8px", color: "#333" }}>
                      Step 3 — Payment Status &amp; Summary
                    </div>
                    <div style={{ background: "#f0fff4", border: "1px solid #1a7a3c", borderRadius: "6px", padding: "12px 16px", marginBottom: "16px" }}>
                      <div className="row" style={{ fontSize: "13px" }}>
                        <div className="col-6">
                          <div style={{ color: "#555" }}>Base Amount</div>
                          <div style={{ fontWeight: 600 }}>₹{Number(billingData.subtotal || 0).toFixed(2)}</div>
                        </div>
                        <div className="col-6">
                          <div style={{ color: "#555" }}>Discount</div>
                          <div style={{ fontWeight: 600, color: "#e65100" }}>- ₹{Number(billingData.discount || 0).toFixed(2)}</div>
                        </div>
                        <div className="col-6 mt-2">
                          <div style={{ color: "#555" }}>
                            GST
                            {gstMode === "bill_level"
                              ? ` — Bill Level @${billGstRate}%`
                              : ` — Per Service (${billingData.gst_percentage}% avg)`}
                          </div>
                          <div style={{ fontWeight: 600, color: "#1a7a3c" }}>
                            ₹{Number((billingData.final_amount || 0) - ((billingData.subtotal || 0) - (billingData.discount || 0))).toFixed(2)}
                          </div>
                        </div>
                        <div className="col-6 mt-2">
                          <div style={{ color: "#555" }}>Final Amount</div>
                          <div style={{ fontWeight: 700, fontSize: "16px", color: "#1a237e" }}>₹{Number(billingData.final_amount || 0).toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Label>Payment Status</Form.Label>
                          <Form.Select value={billingData.payment_status || "UNPAID"}
                            onChange={(e) => setBillingData((p) => ({ ...p, payment_status: e.target.value }))}>
                            <option value="UNPAID">✗ Unpaid</option>
                            <option value="PENDING">⏳ Pending</option>
                            <option value="PAID">✓ Paid</option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Label>GST Method</Form.Label>
                          <Form.Control type="text"
                            value={gstMode === "bill_level"
                              ? `Bill Level — ${billGstRate}% on entire bill`
                              : billingData.gst_percentage > 0
                                ? `Per Service — ${billingData.gst_percentage}% weighted avg`
                                : "Per Service — Not Applicable"}
                            disabled
                            style={{ color: billingData.gst_percentage > 0 ? "#1a7a3c" : "#999", fontWeight: "bold" }}
                          />
                        </Form.Group>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </Modal.Body>

          <Modal.Footer className="d-flex justify-content-between">
            <div>
              {billingStep > 1 && (
                <Button variant="outline-secondary" onClick={() => setBillingStep((s) => s - 1)}>← Back</Button>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button variant="secondary" onClick={handleCloseBillingModal}>Cancel</Button>
              {billingStep < 3
                ? <Button variant="primary" onClick={() => setBillingStep((s) => s + 1)}>Next →</Button>
                : <Button variant="success" onClick={handleCreateBilling}>
                    {isEditingBilling ? "Update Billing & Generate Invoice" : "Create Billing & Generate Invoice"}
                  </Button>
              }
            </div>
          </Modal.Footer>
        </Modal>

        <ToastContainer />
      </Container>
    </div>
  );
};

export default SpaAppointments;